<template>
  <ModalStep
    id="rate-session-modal-content"
    data-test-id="rate-session-modal-content"
    :header="{
      title: t('session.rateSessionModal.title'),
    }"
  >
    <template #bodyContent>
      <div class="c-info-wrapper">
        <p class="c-info-label" style="text-align: center">
          <strong>{{ t('session.rateSessionModal.body') }}</strong>
        </p>
      </div>
      <SessionCard v-if="sessionRef" :session="sessionRef" />
      <div class="stars-container">
        <div :class="`star ${ratingRef >= 1 ? 'activated' : ''}`" @click="setRating(1)">
          <StarIcon color="black" />
        </div>
        <div :class="`star ${ratingRef >= 2 ? 'activated' : ''}`" @click="setRating(2)">
          <StarIcon color="black" />
        </div>
        <div data-test-id="three-star" :class="`star ${ratingRef >= 3 ? 'activated' : ''}`" @click="setRating(3)">
          <StarIcon color="black" />
        </div>
        <div :class="`star ${ratingRef >= 4 ? 'activated' : ''}`" @click="setRating(4)">
          <StarIcon color="black" />
        </div>
        <div :class="`star ${ratingRef >= 5 ? 'activated' : ''}`" @click="setRating(5)">
          <StarIcon color="black" />
        </div>
      </div>

      <div>
        <CustomTextarea
          v-if="programRef?.settings.hasReviewSessionReview"
          :label="`${t('session.rateSessionModal.input.review.label')} (${t('ui.input.label.optional')})`"
          id="review-session"
          :placeholder="`${t('session.rateSessionModal.input.review.placeholder')}`"
          :value="reviewRef"
          @emit:change="onReviewChange"
        />
        <p className="c-info-text">
          {{
            groupRolesRef?.includes(RoleEnumTypeV2.MENTOR)
              ? t('session.rateSessionModal.infoMenteesNoAccess')
              : t('session.rateSessionModal.infoMentorNoAccess')
          }}
        </p>
      </div>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          class="button-fillLater"
          data-test-id="create_session-button-fillLater"
          usage="button"
          type="submit"
          color="white"
          :text="t('ui.button.fillLater')"
          @emit:click="onCloseAndFillLater(doNotAskAgain)"
        />
        <CustomButton
          :is-loading="isReviewSessionPendingRef"
          usage="button"
          data-test-id="button-submit-review-session"
          type="submit"
          color="primary"
          :text="t('ui.button.finish')"
          icon-position="right"
          :is-disabled="ratingRef === 0"
          @emit:click="onSubmit"
        >
          <template #icon>
            <ArrowIcon color="white" />
          </template>
        </CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { reviewSessionMutation } from '@/features/sessions/api'
import SessionCard from '@/features/sessions/components/session-card/session-card.vue'
import { useSessionInReview } from '@/features/sessions/hooks/use-in-review-session.hook'
import useSession from '@/features/sessions/hooks/use-session.hook'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CustomTextarea from '@/features/ui/components/custom-textarea/custom-textarea.vue'
import ArrowIcon from '@/features/ui/components/icons/arrow-icon.vue'
import StarIcon from '@/features/ui/components/icons/star-icon.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import { selectGetReviewSessionModal } from '@/features/ui/store/modal/modal.selectors'
import type { ReviewSessionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import { useI18n } from '@/lib/i18n'
import { DO_NOT_ASK_REVIEW_AGAIN } from '@/utils/config/constants'
import {
  invalidateDeliverable,
  invalidateDeliverables,
  invalidateSession,
  invalidateSessions,
} from '@/utils/lib/vue-query'

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef } = useProgram(selectedProgramIdRef)
const { accountRef } = useAccount()

const ratingRef = ref<number>(0)
const reviewSessionModalRef = selectGetReviewSessionModal(modalsStore)

const reviewRef = ref<string | undefined>(undefined)

function onReviewChange(text: string) {
  reviewRef.value = text
}

const sessionToReviewIdRef = computed(() => reviewSessionModalRef.value.attachedData.sessionId ?? null)
const { sessionRef } = useSession(sessionToReviewIdRef)
const groupIdRef = computed(() => sessionRef.value?.mentoringGroupId ?? null)
const { groupRolesRef } = useGroupRoles({ groupIdRef })
const { t } = useI18n()

const { onClickNextStep, onCloseAndFillLater } = useSessionInReview()

function doNotAskAgain() {
  sessionStorage.setItem(DO_NOT_ASK_REVIEW_AGAIN, 'true')
}

// we set the rating inside the temp attached modal data
function setRating(number: number) {
  ratingRef.value = number
  modalsStore.updateModalAttachedData<ReviewSessionModalType>('review-session', {
    ...reviewSessionModalRef.value.attachedData,
    rating: number,
  })
}

const { mutate: reviewSession, isPending: isReviewSessionPendingRef } = useMutation({
  mutationFn: reviewSessionMutation,
  onError: () => {
    toast?.error(t('error.common'))
  },
  onSuccess: async () => {
    // on success, we invalidate the session
    await invalidateDeliverables({ sessionId: sessionToReviewIdRef.value! })
    await invalidateDeliverable({ userId: accountRef.value!.id!, sessionId: sessionToReviewIdRef.value! })
    await invalidateSessions()
    await invalidateSession(sessionToReviewIdRef.value!)
    toast?.success(t('session.rateSessionModal.success'))

    // we go to the next step
    onClickNextStep()
  },
})

// submiting the note
function onSubmit() {
  reviewSession({
    comment: reviewRef.value ?? '',
    sessionId: sessionToReviewIdRef.value!,
    rating: ratingRef.value,
  })
}
</script>

<style lang="scss">
@import './rate-session-modal-content.scss';
</style>
